import { useEffect, useRef, useState } from 'react'
import { heritage } from '@/content/heritage'
import { useReducedMotion } from '@/state/useReducedMotion'
import HeritageArt from './HeritageArt'
import Icon from '../docs/Icon'
import Cased from '../ui/Cased'

/**
 * The federal design tradition as a timeline of cards, one moment each with
 * its source. The page scrolls past it; the cards move sideways with the
 * Previous and Next buttons, a swipe or a trackpad.
 */
export default function Heritage() {
  const viewport = useRef<HTMLDivElement>(null)
  const back = useRef<HTMLButtonElement>(null)
  const next = useRef<HTMLButtonElement>(null)
  const reduced = useReducedMotion()
  const [edges, setEdges] = useState({ start: true, end: false })

  useEffect(() => {
    const element = viewport.current
    if (!element) return
    const update = () =>
      setEdges({ start: element.scrollLeft <= 1, end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 1 })
    update()
    element.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      element.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  // An arrow hides at its end; focus there moves to the other arrow.
  useEffect(() => {
    if (edges.start && document.activeElement === back.current) next.current?.focus()
    if (edges.end && document.activeElement === next.current) back.current?.focus()
  }, [edges])

  // One card at a time: the width of a card and the gap after it.
  const step = (direction: 1 | -1) => {
    const element = viewport.current
    const card = element?.querySelector<HTMLElement>('.heritage-card')
    if (!element || !card) return
    const gap = parseFloat(getComputedStyle(card.parentElement!).columnGap) || 0
    element.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: reduced ? 'instant' : 'smooth' })
  }

  return (
    <section className="tour heritage" aria-labelledby="heritage-title">
      <div className="tour__sticky">
        <div className="mds-container tour__head">
          <span className="mds-eyebrow">Heritage</span>
          <h2 id="heritage-title" className="mds-display">
            <Cased>Good design is a federal tradition.</Cased>
          </h2>
          <p className="mds-lead heritage__intro">{heritage.intro}</p>
        </div>
        <div className="heritage__carousel">
          <button
            type="button"
            ref={back}
            className="heritage__arrow heritage__arrow--back"
            aria-label="Previous moments"
            hidden={edges.start}
            onClick={() => step(-1)}
          >
            <Icon name="chevron" size={28} />
          </button>
          <button ref={next} type="button" className="heritage__arrow" aria-label="Next moments" hidden={edges.end} onClick={() => step(1)}>
            <Icon name="chevron" size={28} />
          </button>
          <div ref={viewport} className="tour__viewport">
          <ol className="tour__track heritage__track">
            {heritage.moments.map((moment) => (
              <li key={moment.year + moment.title} className="heritage-card">
                <div className="heritage-card__art">
                  <HeritageArt art={moment.art} />
                </div>
                <div className="heritage-card__text">
                  <span className="heritage-card__year">{moment.year}</span>
                  <h3 className="mds-h3"><Cased>{moment.title}</Cased></h3>
                  <p className="mds-muted">{moment.text}</p>
                  <span className="heritage-card__sources">
                    {[moment.source, ...(moment.more ?? [])].map((link) => (
                      <a key={link.url} className="mds-link heritage-card__source" href={link.url}>
                        {link.title}
                      </a>
                    ))}
                  </span>
                </div>
              </li>
            ))}
          </ol>
          </div>
        </div>
      </div>
    </section>
  )
}
